import { CSSProperties, FC, SVGProps, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as AudienceNetworkIcon } from 'src/assets/svg/audience-network.svg'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LinkedInIcon } from 'src/assets/svg/linkedin.svg'
import { ReactComponent as MessengerIcon } from 'src/assets/svg/messenger.svg'
import { ReactComponent as ThreadsIcon } from 'src/assets/svg/threads-square-svg.svg'
import { ReactComponent as TikTokIcon } from 'src/assets/svg/tiktok.svg'
import { ReactComponent as XIcon } from 'src/assets/svg/x.svg'
import { ReactComponent as WhatsAppIcon } from 'src/assets/svg/whatsapp.svg'
import classes from './platformsCell.module.scss'

interface PlatformEntry {
  count: number
  name: string
}

interface PlatformsCellProps {
  platforms: PlatformEntry[]
}

interface PlatformConfig {
  bg: string
  color: string
  icon: FC<SVGProps<SVGSVGElement>>
  label: string
}

const PLATFORM_CONFIG: Record<string, PlatformConfig> = {
  audience_network: {
    bg: 'rgba(128, 128, 255, 0.14)',
    color: 'rgba(128, 128, 255, 1)',
    icon: AudienceNetworkIcon,
    label: 'Audience Network',
  },
  facebook: {
    bg: '#E4E4FF',
    color: 'rgba(128, 128, 255, 1)',
    icon: FacebookIcon,
    label: 'Facebook',
  },
  instagram: {
    bg: '#FFE4EE',
    color: 'rgba(227, 72, 63, 1)',
    icon: InstagramIcon,
    label: 'Instagram',
  },
  linkedin: {
    bg: '#E4F0FF',
    color: 'rgba(40, 103, 178, 1)',
    icon: LinkedInIcon,
    label: 'LinkedIn',
  },
  messenger: {
    bg: '#E4EEFF',
    color: 'rgba(8, 102, 255, 1)',
    icon: MessengerIcon,
    label: 'Messenger',
  },
  threads: {
    bg: '#F0F0F0',
    color: 'rgba(37, 38, 45, 1)',
    icon: ThreadsIcon,
    label: 'Threads',
  },
  tiktok: {
    bg: '#E4FFE4',
    color: 'rgba(37, 38, 45, 1)',
    icon: TikTokIcon,
    label: 'TikTok',
  },
  twitter: {
    // X's mark is black, so the old Twitter-blue chip no longer matches it.
    bg: '#F0F0F0',
    color: 'rgba(37, 38, 45, 1)',
    icon: XIcon,
    label: 'X (Twitter)',
  },
  whatsapp: {
    bg: '#E0FFE6',
    color: 'rgba(37, 211, 102, 1)',
    icon: WhatsAppIcon,
    label: 'WhatsApp',
  },
}

const prettify = (name: string): string =>
  PLATFORM_CONFIG[name]?.label ||
  name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ')

const MAX_VISIBLE = 3

export const PlatformsCell: FC<PlatformsCellProps> = ({ platforms }) => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ right: number; top: number }>({
    right: 0,
    top: 0,
  })
  const btnRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const close = () => setOpen(false)
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !btnRef.current?.contains(e.target as Node) &&
        !popoverRef.current?.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    // Ignore scrolls inside the popover so the list stays scrollable; close
    // on outer page scroll to avoid the fixed-positioned popover detaching.
    const handleScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const knownPlatforms = platforms.filter((p) => p.name in PLATFORM_CONFIG)
  const visible = knownPlatforms.slice(0, MAX_VISIBLE)
  const extraCount = platforms.length - visible.length

  const toggle = () => {
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) {
      setPos({ right: window.innerWidth - rect.right, top: rect.bottom + 6 })
    }
    setOpen((v) => !v)
  }

  return (
    <div className={classes.row}>
      {visible.map((p) => {
        const config = PLATFORM_CONFIG[p.name]
        const Icon = config.icon
        return (
          <div
            className={classes.iconWrapper}
            key={p.name}
            style={
              {
                '--platform-icon-color': config.color,
                background: config.bg,
              } as CSSProperties
            }
            title={`${config.label} · ${p.count}`}
          >
            <Icon className={classes.icon} />
          </div>
        )
      })}
      {extraCount > 0 && (
        <button
          className={classes.more}
          onClick={toggle}
          ref={btnRef}
          type="button"
        >
          +{extraCount}
        </button>
      )}

      {open &&
        createPortal(
          <div
            className={classes.popover}
            ref={popoverRef}
            style={{ right: pos.right, top: pos.top }}
          >
            {platforms.map((p) => {
              const config = PLATFORM_CONFIG[p.name]
              const Icon = config?.icon
              return (
                <div className={classes.item} key={p.name}>
                  {Icon ? (
                    <div
                      className={classes.itemIcon}
                      style={
                        {
                          '--platform-icon-color': config.color,
                          background: config.bg,
                        } as CSSProperties
                      }
                    >
                      <Icon className={classes.icon} />
                    </div>
                  ) : (
                    <div className={classes.itemIconFallback} />
                  )}
                  <span className={classes.itemName}>{prettify(p.name)}</span>
                  <span className={classes.itemCount}>
                    {p.count.toLocaleString()}
                  </span>
                </div>
              )
            })}
          </div>,
          document.body
        )}
    </div>
  )
}
